import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Download, X, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'vexel_pwa_dismissed_at';
const DISMISS_DAYS = 7;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const recentlyDismissed = () => {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && (Date.now() - ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) { return false; }
};

// Pagine PUBBLICHE rivolte ai CLIENTI (Carta Digitale/menu, ordina, tessere,
// prenotazioni, cattura menu): qui NON mostriamo il banner "installa app", che
// riguarda solo il personale del locale.
const PUBLIC_PREFIXES = ['/m/', '/ordina/', '/carta/', '/prenota/', '/cattura/'];
const onPublicCustomerPage = () => {
  try { return PUBLIC_PREFIXES.some((p) => window.location.pathname.startsWith(p)); }
  catch (e) { return false; }
};

/** Banner "Installa app" personalizzato (Chrome/Edge/Android + hint iOS). */
export const PwaInstallPrompt = () => {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed() || onPublicCustomerPage()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      toast.success(t('pwa.installed'));
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS non emette beforeinstallprompt: mostra un suggerimento manuale.
    let timer;
    if (isIOS()) {
      timer = setTimeout(() => { setIosHint(true); setVisible(true); }, 2500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, [t]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) {}
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') setVisible(false);
    } catch (e) { /* ignora */ }
    setDeferred(null);
  }, [deferred]);

  if (!visible || onPublicCustomerPage()) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-0 bottom-0 z-[300] flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pointer-events-none"
    >
      <div className="pointer-events-auto w-full max-w-md rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-xl p-4 flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl overflow-hidden ring-1 ring-border">
          <img src="/icons/icon-192.png" alt="Vexel POS" className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm leading-tight">{t('pwa.installTitle')}</div>
          {iosHint ? (
            <p className="mt-0.5 text-xs text-muted-foreground flex items-center flex-wrap gap-1">
              {t('pwa.iosHintPre')}
              <Share className="h-3.5 w-3.5 inline text-primary" />
              {t('pwa.iosHintMid')}
              <Plus className="h-3.5 w-3.5 inline text-primary" />
              {t('pwa.iosHintPost')}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-muted-foreground">{t('pwa.installText')}</p>
          )}
          {!iosHint && (
            <div className="mt-2.5 flex items-center gap-2">
              <Button size="sm" className="rounded-lg h-8" onClick={install} data-testid="pwa-install-button">
                <Download className="h-4 w-4 mr-1.5" /> {t('pwa.installButton')}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-lg h-8" onClick={dismiss} data-testid="pwa-later-button">
                {t('pwa.later')}
              </Button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="pwa-dismiss-button"
          aria-label={t('pwa.later')}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
